import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { COPY } from "@/data/landingCopy";

const logClientError = (message, error) => {
  if (process.env.NODE_ENV !== "production") {
    console.error(message, error);
  }
};

const updateMetaTag = (selector, content) => {
  if (!content || typeof document === "undefined") {
    return;
  }
  const tag = document.querySelector(selector);
  if (tag) {
    tag.setAttribute("content", content);
  }
};

const LanguageContext = createContext({
  language: "fr",
  setLanguage: () => {},
  copy: COPY.fr,
});

const getInitialLanguage = () => {
  if (typeof window === "undefined") {
    return "fr";
  }
  try {
    const stored = window.localStorage.getItem("nexus-lang");
    if (stored === "fr" || stored === "en") {
      return stored;
    }
  } catch (error) {
    logClientError("Failed to load language preference", error);
  }
  return "fr";
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);

  const copy = useMemo(() => COPY[language] || COPY.fr, [language]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem("nexus-lang", language);
      } catch (error) {
        logClientError("Failed to persist language preference", error);
      }
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
      document.title = copy.meta?.title || document.title;
      updateMetaTag("meta[name=\"description\"]", copy.meta?.description);
      updateMetaTag("meta[property=\"og:title\"]", copy.meta?.ogTitle || copy.meta?.title);
      updateMetaTag(
        "meta[property=\"og:description\"]",
        copy.meta?.ogDescription || copy.meta?.description,
      );
      updateMetaTag("meta[property=\"og:locale\"]", copy.meta?.ogLocale);
    }
  }, [language, copy, logClientError, updateMetaTag]);

  const value = useMemo(() => ({ language, setLanguage, copy }), [language, setLanguage, copy]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);